/**
 * MAU bands — which pricing band a monthly-active-user count falls into. The
 * ONE lookup the subscription page, the daily MAU snapshot + band-crossing
 * push, the quarterly invoice and the monthly recap all run.
 *
 * The bands are data in `platform/pricing.mauBands` (id, min, max, prices)
 * and the platform admin edits them there. Before the 2026-09 audit follow-up
 * (D2) the band-crossing push carried a hard-wired ladder with a "keep in
 * sync" comment, so an edited boundary moved the invoice but not the alert;
 * after it three `findMauBand` copies and three default ladders existed, one
 * per runtime, held together by a parity test. One copy now.
 *
 * Reading the live ladder from Firestore stays per SDK
 * (`loadMauBands` in `apps/functions/shared/mauBands.js`, the `usePricing`
 * listener on the client); everything that decides is here.
 */

/** The default ladder — ids and bounds only; the client adds the prices. */
export const DEFAULT_MAU_BANDS = [
  { id: "band_0", min: 0, max: 100 },
  { id: "band_100", min: 101, max: 300 },
  { id: "band_300", min: 301, max: 1000 },
  { id: "band_1k", min: 1001, max: null },
];

/** The band a count falls into, or null when no band covers it (or no ladder). */
export function findMauBand(mauBands, mauCount) {
  if (!Array.isArray(mauBands)) return null;
  return mauBands.find((b) => mauCount >= b.min && (b.max === null || b.max === undefined || mauCount <= b.max)) || null;
}

/**
 * The band id for a count — the first band when the count is below every
 * band's floor (a fresh gym with 0 MAU and a ladder that starts at 1), the
 * last when it is above every ceiling.
 */
export function mauBandIdFor(mauBands, count) {
  const bands = Array.isArray(mauBands) && mauBands.length ? mauBands : DEFAULT_MAU_BANDS;
  const n = Number(count) || 0;
  const hit = findMauBand(bands, n);
  if (hit) return hit.id;
  return n < bands[0].min ? bands[0].id : bands[bands.length - 1].id;
}

/** "101–300" / "1001–∞" — the same shape the subscription page prints. */
export function mauBandLabelFor(mauBands, bandId) {
  const bands = Array.isArray(mauBands) && mauBands.length ? mauBands : DEFAULT_MAU_BANDS;
  const band = bands.find((b) => b.id === bandId);
  if (!band) return bandId || "";
  return `${band.min}–${band.max == null ? "∞" : band.max}`;
}

/** "up" | "down" | "same" by the bands' own order. */
export function mauBandDirectionFor(mauBands, fromBand, toBand) {
  const bands = Array.isArray(mauBands) && mauBands.length ? mauBands : DEFAULT_MAU_BANDS;
  const a = bands.findIndex((b) => b.id === fromBand);
  const b = bands.findIndex((b2) => b2.id === toBand);
  if (a < 0 || b < 0 || a === b) return "same";
  return b > a ? "up" : "down";
}
